import { useState } from "react";
import { useTranslation } from "react-i18next";

const tiers = [
  { investment: 3000, profit: 30 },
  { investment: 5000, profit: 40 },
  { investment: 10000, profit: 50 },
];

const scenarioMultipliers = [1, 1.2, 1.5];
const scenarioColors = ["#a0845c", "#c9a84c", "#d4af37"];

const whyIcons = ["ri-lock-line", "ri-bank-line", "ri-file-shield-2-line", "ri-global-line"];

export default function Profitability() {
  const { t } = useTranslation();
  const [customAmount, setCustomAmount] = useState(5000);
  const [selectedTier, setSelectedTier] = useState(1);

  const tier = tiers[selectedTier];
  const baseReturn = Math.round(customAmount * (tier.profit / 100));
  const totalPayout = customAmount + baseReturn;
  const progressWidth = (tier.profit / 60) * 100;

  const scenarios = [
    { label: t("profitability.conservative"), multiplier: scenarioMultipliers[0], color: scenarioColors[0] },
    { label: t("profitability.base"), multiplier: scenarioMultipliers[1], color: scenarioColors[1] },
    { label: t("profitability.optimistic"), multiplier: scenarioMultipliers[2], color: scenarioColors[2] },
  ];

  const whyItems = [
    t("profitability.why1"), t("profitability.why2"),
    t("profitability.why3"), t("profitability.why4"),
  ];

  return (
    <section id="profitability" className="bg-[#0b0b0b] py-24 px-6">
      <div className="max-w-7xl mx-auto">

        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="w-8 h-[2px] bg-[#c9a84c]" />
            <span className="text-[#c9a84c] text-xs font-bold tracking-[4px] uppercase">{t("profitability.badge")}</span>
            <div className="w-8 h-[2px] bg-[#c9a84c]" />
          </div>
          <h2 className="text-4xl md:text-5xl font-black text-white mb-4">
            {t("profitability.title1")} <span className="text-[#c9a84c]">{t("profitability.title2")}</span>
          </h2>
          <p className="text-white/60 max-w-xl mx-auto text-base">{t("profitability.desc")}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
          <div className="bg-white/5 border border-white/10 rounded-2xl p-8">
            <h3 className="text-white font-black text-lg mb-6">{t("profitability.calcTitle")}</h3>

            <div className="mb-6">
              <label className="text-white/50 text-xs uppercase tracking-widest font-semibold mb-3 block">
                {t("profitability.selectTier")}
              </label>
              <div className="flex gap-3">
                {tiers.map((ti, i) => (
                  <button
                    key={i}
                    onClick={() => { setSelectedTier(i); setCustomAmount(ti.investment); }}
                    className={`flex-1 py-3 rounded-xl text-sm font-bold transition-all cursor-pointer whitespace-nowrap ${
                      selectedTier === i
                        ? "bg-[#c9a84c] text-[#0b0b0b]"
                        : "bg-white/5 border border-white/10 text-white/70 hover:border-[#c9a84c]/40"
                    }`}
                  >
                    ${ti.investment.toLocaleString()}
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-6">
              <label className="text-white/50 text-xs uppercase tracking-widest font-semibold mb-3 block">
                {t("profitability.customLabel")}
              </label>
              <input
                type="number"
                min={tiers[selectedTier].investment}
                value={customAmount}
                onChange={(e) => setCustomAmount(Math.max(tiers[selectedTier].investment, Number(e.target.value)))}
                className="w-full bg-white/5 border border-white/10 focus:border-[#c9a84c]/60 text-white text-sm rounded-xl px-4 py-3 outline-none transition-colors"
              />
              <p className="text-white/30 text-xs mt-1">
                {t("profitability.minNote", { amount: tiers[selectedTier].investment.toLocaleString() })}
              </p>
            </div>

            <div className="mb-6">
              <div className="flex justify-between mb-2">
                <span className="text-white/50 text-xs uppercase tracking-widest font-semibold">{t("profitability.profitRate")}</span>
                <span className="text-[#c9a84c] font-black">{tier.profit}%</span>
              </div>
              <div className="w-full h-3 bg-white/10 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-[#a0845c] to-[#c9a84c] rounded-full transition-all duration-500"
                  style={{ width: `${progressWidth}%` }}
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="bg-white/5 rounded-xl p-4 text-center">
                <p className="text-white/40 text-xs mb-1">{t("profitability.invested")}</p>
                <p className="text-white font-black text-lg">${customAmount.toLocaleString()}</p>
              </div>
              <div className="bg-white/5 rounded-xl p-4 text-center">
                <p className="text-white/40 text-xs mb-1">{t("profitability.profit")}</p>
                <p className="text-green-400 font-black text-lg">+${baseReturn.toLocaleString()}</p>
              </div>
              <div className="bg-[#c9a84c]/10 border border-[#c9a84c]/30 rounded-xl p-4 text-center">
                <p className="text-[#c9a84c]/70 text-xs mb-1">{t("profitability.total")}</p>
                <p className="text-[#c9a84c] font-black text-lg">${totalPayout.toLocaleString()}</p>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-6">
            <div className="bg-white/5 border border-white/10 rounded-2xl p-8">
              <h3 className="text-white font-black text-lg mb-6">{t("profitability.scenarioTitle")}</h3>
              <div className="space-y-5">
                {scenarios.map((s) => {
                  const scenReturn = Math.round(customAmount * (tier.profit / 100) * s.multiplier);
                  const scenTotal = customAmount + scenReturn;
                  const barW = Math.min(100, (scenReturn / (customAmount * 0.8)) * 100);
                  return (
                    <div key={s.label}>
                      <div className="flex justify-between mb-2">
                        <span className="text-white/70 text-sm font-semibold">{s.label}</span>
                        <span className="font-black text-sm" style={{ color: s.color }}>
                          ${scenTotal.toLocaleString()} (+${scenReturn.toLocaleString()})
                        </span>
                      </div>
                      <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
                        <div className="h-full rounded-full transition-all duration-700" style={{ width: `${barW}%`, backgroundColor: s.color }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-2xl p-8">
              <h3 className="text-white font-black text-lg mb-5">{t("profitability.whyTitle")}</h3>
              <ul className="space-y-4">
                {whyItems.map((text, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-8 h-8 flex items-center justify-center bg-[#c9a84c]/10 rounded-lg shrink-0 mt-0.5">
                      <i className={`${whyIcons[i]} text-[#c9a84c] text-sm`} />
                    </div>
                    <p className="text-white/60 text-sm leading-relaxed">{text}</p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
